import * as _ from 'lodash';
import { Component, effect, EventEmitter, HostBinding, HostListener, inject, InputSignal, OnInit, Output, TemplateRef, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';

import { ActivatedRouteService } from '../../services/route-service/activated-route.service';
import { NavigationStackService } from 'src/app/features/shared/navigation-stack/services/navigation-stack.service';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { DialogService } from 'src/app/features/shared/dialogs/dialog.service';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { headerData, IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IPageData } from '../../services/page-services/models/page-data';
import { ISectionButtonData } from 'src/app/features/shared/central-page/components/button-blocks/models/_base-section-button';
import { LoadingPageService } from '../../services/loading-services/loading-page.service';
import { LoadingService } from '../../services/loading-services/_loading.service';
import { pageMode, TPageMode } from '../../models/page-mode';
import { ReloadService } from '../../services/route-service/reload.service';
import { TRoutePage } from '../../services/route-service/models/route-page';
import { ApplicationUserService } from '../../services/application-user.services/application-user.service';
import { _FormModelService } from '../../services/model-services/_form-model.service';
import { _PageService } from '../../services/page-services/_page.service';
import { pageLayout, TPageLayout } from '../../models/page-layout';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

@Component({
    selector: 'app-page-component',
    template: '',
    styles: [],
})
export abstract class _BaseRouteComponent
    implements OnInit
{
    abstract readonly userClaimService: ApplicationUserService;    // Put always first in row
    abstract readonly activatedRouteService: ActivatedRouteService | null;
    abstract readonly centralPageService: _CentralPageService;
    abstract readonly footerService: FooterService;
    abstract readonly formModelService: _FormModelService | null;
    abstract readonly loadingPageService: LoadingPageService;
    abstract readonly loadingService: LoadingService;
    abstract readonly pageService: _PageService;
    abstract readonly reloadService: ReloadService;

    abstract btnCancelData: ISectionButtonData | null;
    abstract btnDeleteData: ISectionButtonData | null;
    abstract btnEditData: ISectionButtonData | null;
    abstract btnSaveData: ISectionButtonData | null;
    abstract isStartPage: boolean;
    abstract pageCurrent_name: TRoutePage;
    abstract pageLayout: TPageLayout;
    abstract pageMode_initial: TPageMode;
    abstract returnPage: IPageData | null;
    
    abstract template_details$$: () => TemplateRef<any> | undefined;
    abstract template_edit$$: () => TemplateRef<any> | undefined;
    abstract template_footerLeft$$: () => TemplateRef<any> | undefined;

    abstract ngOnInit(): void;
    abstract emit_onEscape(): void;
    abstract emit_onPageLoaded(): void;

    abstract headerData: Partial<IHeaderData>;
    abstract footerData: Partial<IFooterData>;

    protected readonly _applicationUserService = injectRequired(ApplicationUserService);    // Put always first in row
    protected readonly _activatedRouteService = inject(ActivatedRouteService, {optional: true});
    protected readonly _centralPageService = injectRequired(_CentralPageService);
    protected readonly _dialogService = injectRequired(DialogService);
    protected readonly _footerService = injectRequired(FooterService);
    protected readonly _headerService = injectRequired(HeaderService);
    protected readonly _formModelService = inject(_FormModelService, {optional: true});
    protected readonly _loadingPageService = injectRequired(LoadingPageService);
    protected readonly _loadingService = injectRequired(LoadingService);
    protected readonly _pageService = injectRequired(_PageService);
    protected readonly _navigationStackService = injectRequired(NavigationStackService);
    protected readonly _reloadService = injectRequired(ReloadService);
    protected readonly _router = injectRequired(Router);

    // Welke pagina-identiteit de navigatiestack (dus ook page-parent.ts's voorouder-opzoeking)
    // gebruikt voor DIT bezoek — standaard gelijk aan pageCurrent_name. Losstaand overrideable voor
    // de zeldzame component die op meer dan één echte route wordt gerenderd met een STATISCHE
    // pageCurrent_name (bv. FunctionComponent, zie diens override): pageCurrent_name blijft daar
    // ongewijzigd voor andere doeleinden (nav-history-descriptor-lookup, claim-page-checks, ...),
    // maar de stack moet WEL weten via welke van de twee routes dit bezoek daadwerkelijk liep, anders
    // synthetiseert __ancestors() een voorouder die niet bij de echte url hoort.
    protected get pageCurrent_forStack(): TRoutePage {
        return this.pageCurrent_name;
    }

    protected _template_details_viewChild$$ = viewChild<TemplateRef<any>>('template_details');
    protected _template_edit_viewChild$$ = viewChild<TemplateRef<any>>('template_edit');
    protected _template_footerLeft_viewChild$$ = viewChild<TemplateRef<any>>('template_footerLeft');

    @Output() escEmit = new EventEmitter();

    @HostBinding('class') class = 'route-page';

    constructor() {
        effect(() => this._centralPageService.sectionDetails_template_set(this.template_details$$()))
        effect(() => this._centralPageService.sectionEdit_template_set(this.template_edit$$()))
        effect(() => this._footerService.template_buttonLeft_set(this.template_footerLeft$$()))
    }

    protected _ngOnInit(): void {
        // Authoritatieve bron voor "huidige pagina" is dit component (pageCurrent_name), niet de
        // _PageService-instantie zelf: die kan hergebruikt worden over meerdere componenten (bv.
        // AccountPageService voor zowel Home als Login) en zou anders op een vaste, verkeerde
        // pagina blijven staan. Moet vóór PageStart/pageData_store hieronder, die er beide van afhangen.
        this._pageService.pageCurrent_set(this.pageCurrent_name);

        this._loadingService.reset();
        this._loadingPageService.add();
        const _pageMode_initial = this.pageLayout == pageLayout.listPage ? this.pageLayout : this.pageMode_initial;

        this._centralPageService.pageLayout_set(this.pageLayout);
        this._centralPageService.pageMode_set(_pageMode_initial);
        this._centralPageService.pageMode_initial_set(_pageMode_initial);

        this._pageService.headerData_set(this.headerData);
        this._pageService.footerData_set(this.footerData);

        // Vroege, synchrone push naar de zichtbare header (naast de staged page-data hierboven,
        // die pas ná montage van de sectie-component wordt overgenomen). Zonder dit blijft de
        // header tot dan de sideBlockLeft/Right_hide-stand van de vórige pagina tonen — zichtbaar
        // geflikker bij navigatie (bv. Home/AdminPanel/Login t.o.v. een normale feature-pagina).
        this._headerService.headerData_set({
            [headerData.sideBlockLeft_hide]: false,
            [headerData.sideBlockRight_hide]: false,
            [headerData.sideBlockLeft_noClick]: false,
            ...this.headerData,
        });
        this._pageService.PageStart = this.isStartPage;

        // Registreer dit bezoek in de navigatiestack. Dat bepaalt vanaf hier zowel de bestemming van
        // de terugknop (het frame eronder) als welke tab-/lijststate bij dít bezoek hoort — vandaar
        // dat opnieuw afdalen vanzelf een schone pagina oplevert.
        this._navigationStackService.enter(
            this.pageCurrent_forStack,
            this._activatedRouteService?.routeKeys ?? null,
            this._router.url,
        );

        this.__centralPageButtons_set();
    }

    protected _emit_onEscape(): void {
        this._pageService.pageReturn_goto();
    }

    // Sneltoetsen voor de vier standaardknoppen (edit/save/delete/cancel), alleen actief zolang de
    // knop zelf niet hide/disabled is — precies dezelfde voorwaarde als de knop op het scherm.
    // Cancel loopt al via de bestaande Escape-handler in _section-form-component.ts.
    @HostListener('document:keydown', ['$event']) handleShortcutKeys(event: KeyboardEvent) {
        if (!event.ctrlKey || event.shiftKey || event.altKey) return;

        const __key = event.key.toLowerCase();

        if (__key === 'e') {
            if (this._centralPageService.btnEditData$$().hide || this._centralPageService.btnEditData$$().disabled) return;
            event.preventDefault();
            this._centralPageService.pageMode_set(pageMode.editPage);
        } else if (__key === 's') {
            if (this._centralPageService.btnSaveData$$().hide || this._centralPageService.btnSaveData$$().disabled) return;
            event.preventDefault();
            this._centralPageService.onSave_emit();
        } else if (__key === 'delete') {
            if (this._centralPageService.btnDeleteData$$().hide || this._centralPageService.btnDeleteData$$().disabled) return;
            event.preventDefault();
            this.__deleteWithConfirm();
        }
    }

    // Spiegelt exact de bevestigingsdialoog uit button-block-details-section.component.ts —
    // de sneltoets mag de destructieve delete-actie niet zonder bevestiging uitvoeren.
    private __deleteWithConfirm(): void {
        this._dialogService
            .showChoice<boolean>(
                [
                    { label: 'Verwijderen', value: true },
                    { label: 'Annuleren', value: false },
                ],
                'Weet je zeker dat je dit wilt verwijderen?',
            )
            .pipe(take(1))
            .subscribe(confirmed => {
                if (confirmed === true) this._centralPageService.onDelete_emit();
            });
    }

    _emit_onPageLoaded() {
        // this._centralPageService.pageMode_set(pageMode.detailsPage);
        this._loadingPageService.remove();
    }

    private __centralPageButtons_set() {
        this._centralPageService.btnCancelData_set(this.btnCancelData);
        this._centralPageService.btnDeleteData_set(this.btnDeleteData);
        this._centralPageService.btnEditData_set(this.btnEditData);
        this._centralPageService.btnSaveData_set(this.btnSaveData);
        // De autorisatiepagina van de details-/edit-sectie wordt door de sectie-componenten zelf
        // omhoog geduwd via hun CLAIM_PAGE-token (zie _SectionBaseComponent).
    }
}
